/**
 * The Google scopes each kind of account signs in with.
 *
 * This list lived in two places — the sign-in screen and the settings
 * connect button — which is how the two drifted and how a scope change could
 * half-land. One module, both callers.
 */

/**
 * What a creator grants.
 *
 * `youtube.readonly` covers the Data API: channels, videos, public counts.
 * It does NOT authorise the YouTube Analytics API, which is where audience
 * demographics, watch time, traffic sources and subscriber movement come
 * from — that needs `yt-analytics.readonly`. Without it every Analytics call
 * the backend makes answers 403, which is exactly what was happening: the
 * calls existed, the scope did not, and the failures were swallowed.
 *
 * Deliberately absent: `yt-analytics-monetary.readonly`. It renders on the
 * consent screen as "see your YouTube revenue" and is the single biggest
 * drop-off trigger in a creator funnel. We do not need RPM for pricing.
 */
export const CREATOR_SCOPES = [
  'openid',
  'email',
  'profile',
  'https://www.googleapis.com/auth/userinfo.email',
  'https://www.googleapis.com/auth/userinfo.profile',
  'https://www.googleapis.com/auth/youtube.readonly',
  'https://www.googleapis.com/auth/yt-analytics.readonly',
];

/** Brands connect Google Analytics on top of the same base. */
export const BRAND_SCOPES = [
  ...CREATOR_SCOPES,
  'https://www.googleapis.com/auth/analytics.readonly',
];

export const YT_ANALYTICS_SCOPE = 'https://www.googleapis.com/auth/yt-analytics.readonly';

export function scopesFor(userType) {
  return userType === 'brand' ? BRAND_SCOPES : CREATOR_SCOPES;
}

/**
 * The options every Google auth-code call shares.
 *
 * `access_type: 'offline'` with `prompt: 'consent'` is what makes Google
 * return a refresh token, without which background fetching stops the moment
 * the access token expires. `include_granted_scopes` makes a returning
 * creator's new grant additive, so re-consenting to add analytics does not
 * drop something they already gave us.
 */
export const GOOGLE_AUTH_OPTIONS = {
  flow: 'auth-code',
  access_type: 'offline',
  prompt: 'consent',
  include_granted_scopes: true,
};
